import { VisitsModel } from '@w3lcome/types';
import appConfig from '_/config/app';
import logger from '_/services/logger';
import { AxiosRequestConfig } from 'axios';

import { CheckinInterface } from './../../interfaces/CheckinInterface';
import RequestCreator from './BaseRequest';

interface VisitType {
  total: number;
  limit: number;
  skip: number;
  data: VisitsModel[];
}

class VisitsApi extends RequestCreator<VisitsModel> {
  private primaryKeyPath = 'ipad/primary-keys';
  private checkinPath = '/ipad/checkin';
  private childrenCheckinPath = 'ipad/children/checkin';
  private childrenPrimaryKeyPath = 'ipad/children/primary-keys';
  private childrenVisitsPath = 'ipad/children/visits';

  childrenVisitsUpdate = async (
    id: string,
    values: Partial<VisitsModel>,
    config?: AxiosRequestConfig
  ) => {
    try {
      const { data } = await this.api.patch(`${this.childrenVisitsPath}/${id}`, values, config);
      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  checkin = async (values: CheckinInterface, config?: AxiosRequestConfig) => {
    try {
      const { data } = await this.api.post(`${this.checkinPath}`, values, {
        timeout: appConfig.checkinApiTimeout,
        ...config,
      });
      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  childrenCheckin = async (values: CheckinInterface, config?: AxiosRequestConfig) => {
    try {
      const { data } = await this.api.post(`${this.childrenCheckinPath}`, values, {
        timeout: appConfig.checkinApiTimeout,
        ...config,
      });
      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  getChildrenVisits = async (config?: AxiosRequestConfig) => {
    try {
      const { data } = await this.api.get(`${this.childrenVisitsPath}`, config);
      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  getChildrenVisit = async (id: string, config?: AxiosRequestConfig) => {
    try {
      const { data } = await this.api.get(`${this.childrenVisitsPath}/${id}`, config);
      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  searchVisitToCheckout = async (name: string) => {
    try {
      const params = {
        'name[$iLike]': `%${name}%`,
        checkoutAt: '<null>',
        'name[$ne]': '<null>',
        'checkinAt[$ne]': '<null>',
      };
      const result = await this.api.get<VisitType>(`${this.path}`, {
        params,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      });

      return result.data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  searchChildrenVisitToCheckout = async (name: string, params?: any) => {
    try {
      const paramsData = {
        'name[$iLike]': `%${name}%`,
        checkoutAt: '<null>',
        'name[$ne]': '<null>',
        'checkinAt[$ne]': '<null>',
        ...params,
      };

      const result = await this.api.get<VisitType>(`${this.childrenVisitsPath}`, {
        params: paramsData,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      });

      return result.data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  getVisitByPrimaryKey = async (
    value: string,
    visitFlowTypeId: string,
    isVisitorForeigner: boolean
  ): Promise<{ visit: VisitsModel; canceledVisits?: VisitsModel[]; visits?: VisitsModel[] }> => {
    try {
      const { data } = await this.api.get(`${this.primaryKeyPath}/${value}`, {
        params: { visitFlowTypeId, isVisitorForeigner },
      });

      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  getVisitByPrimaryKeyAllFlowType = async (
    value: string,
    visitFlowTypeId: string,
    visitFlowTypes: string[],
    isVisitorForeigner: boolean
  ): Promise<{ visit: VisitsModel; canceledVisits?: VisitsModel[]; visits?: VisitsModel[] }> => {
    try {
      const { data } = await this.api.get(`${this.primaryKeyPath}/${value}`, {
        params: { visitFlowTypes, visitFlowTypeId, isVisitorForeigner },
      });

      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };

  getChildrenVisitByPrimaryKey = async (
    value: string,
    companyId: string,
    visitFlowTypeId: string,
    isVisitorForeigner: boolean
  ): Promise<{ visit: VisitsModel; canceledVisits?: VisitsModel[]; visits?: VisitsModel[] }> => {
    try {
      const { data } = await this.api.get(`${this.childrenPrimaryKeyPath}/${value}`, {
        params: { companyId, visitFlowTypeId, isVisitorForeigner },
      });

      return data;
    } catch (error) {
      logger(error);
      throw error;
    }
  };
}

const api = new VisitsApi('/ipad/visits');
export default api;
